// src/pages/temples/TempleStatisticsImportModal.jsx
// Modal นำเข้าสถิติประจำปีจาก Excel: เลือกไฟล์ -> ตรวจสอบ -> Import bulk + Progress bar
import React, { useEffect, useState } from 'react';
import { Modal, Button, Upload, Progress, List, Table, Alert, Tag, Space, Typography, message } from 'antd';
import {
  InboxOutlined, FileExcelOutlined, CheckCircleOutlined, CloseCircleOutlined,
  ReloadOutlined, ArrowLeftOutlined, UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { STAT_COLUMNS, NUMERIC_FIELDS } from '../../utils/excelTemplate';
import { parseThaiNumber, thaiToArabicDigits } from '../../utils/thaiDigits';

const { Text } = Typography;
const BATCH_SIZE = 50; // จำนวนรายการต่อชุด (bulk)

// ============ หัวคอลัมน์แบบยืดหยุ่น (รองรับแบบฟอร์มหัวตาราง 2 แถว) ============

// ตรวจว่าเซลล์เป็นตัวเลขจริงหรือไม่ (แปลงเลขไทยได้ ไม่นับข้อความ)
function looksNumeric(v) {
  if (v === '' || v === undefined || v === null) return false;
  if (typeof v === 'number') return Number.isFinite(v);
  const s = thaiToArabicDigits(String(v)).replace(/[,\s]/g, '');
  return /^-?\d+(\.\d+)?$/.test(s);
}

// จับคู่หัวคอลัมน์ -> ชื่อฟิลด์ (key) โดยรองรับทั้งภาษาอังกฤษ/ภาษาไทย และ [key]
function headerToKey(cellText) {
  const t = String(cellText ?? '').trim();
  if (!t) return null;
  const lower = t.toLowerCase();

  // 1) ตรงกับรหัสฟิลด์ภาษาอังกฤษ (temple_id, year, ...) — รวมกรณีอยู่ในวงเล็บ [key]
  for (const c of STAT_COLUMNS) {
    const k = c.key.toLowerCase();
    if (lower === k || lower.includes(k)) return c.key;
  }

  // 2) ตรงกับ alias ภาษาไทย (เรียงจากยาวสุดก่อนเพื่อลดการชนกัน)
  const candidates = [];
  for (const c of STAT_COLUMNS) {
    for (const a of c.aliases || []) candidates.push({ alias: String(a).toLowerCase(), key: c.key });
  }
  candidates.sort((a, b) => b.alias.length - a.alias.length);
  for (const { alias, key } of candidates) {
    if (alias.length >= 4 ? lower.includes(alias) : lower === alias) return key;
  }

  // 3) ตรงกับชื่อ label ไทย (ตัดส่วนวงเล็บออก)
  for (const c of STAT_COLUMNS) {
    const main = String(c.label || '').replace(/\s*\(.*?\)\s*/g, '').trim().toLowerCase();
    if (main.length >= 3 && (lower.includes(main) || main.includes(lower))) return c.key;
  }

  return null;
}

// ตรวจว่าแถวนี้เป็นแถวหมวดหมู่ (หัวตารางแถวบนที่ merge) หรือไม่
function isGroupRow(row, totalColumns) {
  const nonEmpty = (row || []).filter((v) => String(v ?? '').trim() !== '');
  if (nonEmpty.length === 0) return false;
  const firstText = String(nonEmpty[0] ?? '').trim();
  // ขึ้นต้นด้วยเลขไทย/เลขอารบิก + จุด หรือคำว่าหมวด/ตอน/ส่วน/ข้อ
  if (/^[๐-๙0-9][๐-๙0-9]*\s*[.]/.test(firstText)) return true;
  if (/^(หมวด|ตอนที่|ส่วนที่|ข้อที่)\s/.test(firstText)) return true;
  // แถวที่บางมาก (มีเซลล์น้อยเมื่อเทียบกับความกว้างรวม) -> น่าจะเป็นแถวหมวดหมู่ที่ merge
  if (totalColumns >= 5 && nonEmpty.length <= Math.max(1, Math.floor(totalColumns / 3))) return true;
  return false;
}

// สแกนหัวตารางหลายแถว (ข้ามแถวหมวดหมู่) -> { colIndex, dataStart }
function buildColumnIndex(rawRows) {
  const colIndex = {};
  const maxScan = Math.min(rawRows.length, 5);
  const totalColumns = Math.max(1, ...rawRows.slice(0, maxScan).map((r) => (r || []).length));
  let dataStart = -1;

  for (let i = 0; i < maxScan; i++) {
    const row = rawRows[i] || [];

    // ถ้ารู้ตำแหน่ง temple_id แล้ว และแถวนี้ค่าเป็นตัวเลข -> แถวข้อมูล
    const tidCol = colIndex.temple_id;
    if (tidCol !== undefined && looksNumeric(row[tidCol])) {
      dataStart = i;
      break;
    }

    if (isGroupRow(row, totalColumns)) continue; // ข้ามแถวหมวดหมู่

    // จับคู่หัวคอลัมน์ในแถวนี้
    row.forEach((cell, ci) => {
      const key = headerToKey(cell);
      if (key && colIndex[key] === undefined) colIndex[key] = ci;
    });
  }

  // fallback: ยังหาแถวข้อมูลไม่เจอ -> เริ่มจากแถวที่เลยหัวตารางที่สแกนไป
  if (dataStart === -1) dataStart = maxScan;
  return { colIndex, dataStart };
}

// แปลง Excel serial date (ตัวเลข) -> YYYY-MM-DD
function serialToDate(serial) {
  if (typeof serial !== 'number' || serial < 20000 || serial > 60000) return String(serial);
  const d = new Date(Math.round((serial - 25569) * 86400 * 1000));
  if (Number.isNaN(d.getTime())) return String(serial);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// แปลงค่าที่อ่านจาก Excel (sendAt) ให้เป็นวันที่ YYYY-MM-DD
// รองรับ: Excel serial number, Date object, ISO datetime "2026-08-03T00:00:00.000Z", "2026-08-03 12:00:00"
function toDateString(v) {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'number') return serialToDate(v);
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  // "2026-08-03T00:00:00.000Z" / "2026-08-03 12:00:00" / "2026-08-03"
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  // "2026/08/03"
  const slash = s.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  return s;
}

// ตรวจสอบแถวเดียว (จาก object ที่ normalize header แล้ว)
function validateRow(rec, excelRow, templeMap, seen) {
  const errors = [];
  const out = { excelRow };

  let tid = rec.temple_id;
  if (tid === '' || tid === undefined || tid === null) {
    errors.push('temple_id ว่างหรือไม่ถูกต้อง');
  } else {
    tid = parseThaiNumber(tid);
    if (!tid) {
      errors.push('temple_id ไม่ใช่ตัวเลข');
    } else if (!templeMap.has(Number(tid))) {
      errors.push(`ไม่พบวัด id ${tid} ในระบบ — ต้องเพิ่มรายชื่อวัดก่อนเพื่อให้ได้ Primary key`);
    } else {
      out.temple_id = Number(tid);
    }
  }

  let year = rec.year;
  if (year === '' || year === undefined || year === null) {
    errors.push('year ว่างหรือไม่ถูกต้อง');
  } else {
    year = parseThaiNumber(year);
    if (!year) {
      errors.push('year ไม่ใช่ตัวเลข');
    } else {
      if (year > 2500) year -= 543; // พ.ศ. -> ค.ศ.
      if (year < 2000 || year > 2100) {
        errors.push(`year (${year}) อยู่นอกช่วงที่รองรับ (ค.ศ. 2000-2100)`);
      } else {
        out.year = year;
      }
    }
  }

  if (out.temple_id && out.year) {
    const key = `${out.temple_id}|${out.year}`;
    if (seen.has(key)) errors.push('รายการซ้ำ (temple_id + year เดิม) ในไฟล์');
    else seen.add(key);
  }

  for (const f of NUMERIC_FIELDS) {
    if (f === 'temple_id' || f === 'year') continue;
    const v = rec[f];
    if (v !== '' && v !== undefined && v !== null) {
      const n = parseThaiNumber(v);
      if (!Number.isFinite(n)) errors.push(`${f} ไม่ใช่ตัวเลข`);
      else out[f] = n;
    }
  }

  for (const c of STAT_COLUMNS) {
    if (c.type !== 'text' || c.key === 'temple_name') continue;
    const v = rec[c.key];
    if (v !== '' && v !== undefined && v !== null) {
      out[c.key] = c.key === 'sendAt' ? toDateString(v) : String(v);
    }
  }

  return { out, errors };
}

const TempleStatisticsImportModal = ({ open, onClose, onImported }) => {
  const [step, setStep] = useState('select'); // select | validate | importing | done
  const [temples, setTemples] = useState([]);
  const [templeMap, setTempleMap] = useState(new Map());
  const [rawFileName, setRawFileName] = useState('');
  const [validRows, setValidRows] = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [importLog, setImportLog] = useState([]);
  const [progress, setProgress] = useState({ percent: 0, current: 0, total: 0 });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep('select');
    setRawFileName('');
    setValidRows([]);
    setInvalidRows([]);
    setImportLog([]);
    setProgress({ percent: 0, current: 0, total: 0 });
    setSummary(null);
    if (temples.length === 0) {
      (async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/api/get-temples`);
          setTemples(res.data.temples || []);
          setTempleMap(new Map((res.data.temples || []).map((t) => [Number(t.id), t])));
        } catch (err) {
          message.error('ไม่สามารถโหลดรายชื่อวัดได้');
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // อ่านไฟล์ Excel + ตรวจสอบเบื้องต้น
  const handleFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets['สถิติประจำปี'] || wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        message.error('ไม่พบแผ่นข้อมูลในไฟล์');
        return;
      }
      const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (!rawRows.length) {
        message.error('ไม่พบข้อมูลในไฟล์');
        return;
      }
      // จับคู่หัวคอลัมน์แบบยืดหยุ่น: รองรับหัวตาราง 2 แถว (หมวดหมู่ + ชื่อคอลัมน์ ไทย/อังกฤษ)
      const { colIndex, dataStart } = buildColumnIndex(rawRows);
      if (colIndex.temple_id === undefined) {
        message.error('ไม่พบคอลัมน์ temple_id / "รหัสวัด" — กรุณาใช้ไฟล์จากปุ่ม "Export Excel" (หัวคอลัมน์ 2 แถว)');
        return;
      }
      const dataRows = rawRows
        .slice(dataStart)
        .filter((r) => (r || []).some((v) => v !== '' && v !== undefined));
      if (!dataRows.length) {
        message.error('ไม่พบแถวข้อมูลในไฟล์');
        return;
      }
      const seen = new Set();
      const valid = [];
      const invalid = [];
      dataRows.forEach((rowArr, idx) => {
        const rec = {};
        for (const c of STAT_COLUMNS) {
          const ci = colIndex[c.header];
          rec[c.key] = ci !== undefined && rowArr[ci] !== undefined ? rowArr[ci] : '';
        }
        const { out, errors } = validateRow(rec, dataStart + idx + 1, templeMap, seen);
        if (errors.length) {
          invalid.push({
            excelRow: dataStart + idx + 1,
            temple_id: rec.temple_id === '' ? '-' : rec.temple_id,
            temple_name: rec.temple_name || templeMap.get(Number(rec.temple_id))?.name || '-',
            errors,
          });
        } else {
          valid.push(out);
        }
      });
      setValidRows(valid);
      setInvalidRows(invalid);
      setRawFileName(file.name);
      setStep('validate');
    } catch (err) {
      message.error('อ่านไฟล์ไม่สำเร็จ: ' + err.message);
    }
  };


  // เริ่มนำเข้าแบบ bulk (เป็นชุด ๆ พร้อม progress bar + log)
  const handleStartImport = async () => {
    setStep('importing');
    setProgress({ percent: 0, current: 0, total: validRows.length });
    setImportLog([]);
    let log = [];
    const total = validRows.length;
    const totalBatches = Math.ceil(total / BATCH_SIZE);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      for (let b = 0; b < totalBatches; b++) {
        const batch = validRows.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
        const res = await axios.post(`${API_BASE_URL}/api/temple-statistics/import`, { records: batch }, { headers });
        const results = res.data.results || [];
        const batchLog = results.map((r) => {
          const rec = batch[r.index] || {};
          const t = rec.temple_id ? templeMap.get(Number(rec.temple_id)) : null;
          return {
            excelRow: rec.excelRow || '-',
            temple_id: rec.temple_id || '-',
            temple_name: t ? t.name : '',
            status: r.status,
            message: r.message,
          };
        });
        log = [...log, ...batchLog];
        setImportLog(log);
        const processed = Math.min((b + 1) * BATCH_SIZE, total);
        setProgress({ percent: Math.round((processed / total) * 100), current: processed, total });
      }
    } catch (err) {
      message.error('Import ล้มเหลว: ' + (err.response?.data?.error || err.message));
    }
    setSummary({
      success: log.filter((r) => r.status === 'success').length,
      error: log.filter((r) => r.status === 'error').length,
    });
    setStep('done');
  };

  const invalidColumns = [
    { title: 'แถวที่', dataIndex: 'excelRow', key: 'excelRow', width: 70 },
    { title: 'temple_id', dataIndex: 'temple_id', key: 'temple_id', width: 110 },
    { title: 'ชื่อวัด', dataIndex: 'temple_name', key: 'temple_name', width: 180 },
    {
      title: 'เหตุผล',
      dataIndex: 'errors',
      key: 'errors',
      render: (errs) => (
        <div>
          {errs.map((e, i) => (
            <Tag key={i} color="red" style={{ marginBottom: 2 }}>{e}</Tag>
          ))}
        </div>
      ),
    },
  ];

  const logColumns = [
    { title: 'แถวที่', dataIndex: 'excelRow', key: 'excelRow', width: 70 },
    { title: 'temple_id', dataIndex: 'temple_id', key: 'temple_id', width: 100 },
    { title: 'ชื่อวัด', dataIndex: 'temple_name', key: 'temple_name', width: 190 },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) =>
        s === 'success' ? <Tag color="green">สำเร็จ</Tag> : <Tag color="red">ผิดพลาด</Tag>,
    },
    { title: 'รายละเอียด', dataIndex: 'message', key: 'message' },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span><FileExcelOutlined style={{ marginRight: 8 }} />นำเข้าสถิติประจำปีจาก Excel</span>}
      width={860}
      footer={null}
    >
      {/* ขั้น 1: เลือกไฟล์ */}
      {step === 'select' && (
        <div>
          <Alert
            type="info"
            showIcon
            message="ขั้นตอนการใช้งาน"
            description={'1) กด "Export Excel" เพื่อดาวน์โหลดไฟล์ต้นแบบ (หัวคอลัมน์ 2 แถว)  2) เปิดไฟล์แล้วกรอกข้อมูลในแผ่น "สถิติประจำปี"  3) นำไฟล์กลับมาอัปโหลดที่นี่ ระบบจะตรวจสอบและนำเข้า (จับคู่คอลัมน์อัตโนมัติ รองรับชื่อไทย/อังกฤษ และข้ามแถวหมวดหมู่)'}
            style={{ marginBottom: 16 }}
          />
          <Upload.Dragger
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => { handleFile(file); return false; }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">คลิกหรือลากไฟล์ Excel (.xlsx) มาที่นี่</p>
            <p className="ant-upload-hint">
              ใช้ไฟล์จากปุ่ม "Export Excel" ที่กรอกข้อมูลแล้ว — วัดใหม่ที่ยังไม่มีในระบบต้องเพิ่มรายชื่อก่อน (เพื่อให้ได้ temple_id)
            </p>
          </Upload.Dragger>
        </div>
      )}

      {/* ขั้น 2: ตรวจสอบความถูกต้อง */}
      {step === 'validate' && (
        <div>
          <Alert
            type={invalidRows.length ? 'warning' : 'success'}
            showIcon
            message={`ไฟล์: ${rawFileName}`}
            description={`ตรวจสอบแล้ว — แถวทั้งหมด ${validRows.length + invalidRows.length} แถว | ผ่าน ${validRows.length} แถว | ผิดพลาด ${invalidRows.length} แถว`}
            style={{ marginBottom: 12 }}
          />
          {invalidRows.length > 0 && (
            <>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                รายการที่ไม่ผ่านการตรวจสอบ (แก้ไขในไฟล์แล้วเลือกใหม่ หรือเพิ่มวัดก่อนแล้วจึง import):
              </Text>
              <Table
                columns={invalidColumns}
                dataSource={invalidRows}
                rowKey="excelRow"
                size="small"
                pagination={false}
                scroll={{ y: 220 }}
              />
            </>
          )}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => setStep('select')}>
                เลือกไฟล์ใหม่
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                disabled={validRows.length === 0}
                onClick={handleStartImport}
              >
                เริ่ม Import ({validRows.length} รายการ)
              </Button>
            </Space>
          </div>
        </div>
      )}

      {/* ขั้น 3: กำลังนำเข้า (bulk + progress bar) */}
      {step === 'importing' && (
        <div>
          <Progress percent={progress.percent} status="active" />
          <Text type="secondary">
            กำลังนำเข้า {progress.current} / {progress.total} รายการ (ชุดละ {BATCH_SIZE} รายการ)
          </Text>
          <Table
            columns={logColumns}
            dataSource={importLog}
            rowKey="excelRow"
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
            style={{ marginTop: 16 }}
            locale={{ emptyText: 'กำลังเริ่มนำเข้า...' }}
          />
        </div>
      )}

      {/* ขั้น 4: เสร็จสิ้น */}
      {step === 'done' && summary && (
        <div>
          <Alert
            type={summary.error ? 'warning' : 'success'}
            showIcon
            message="Import เสร็จสิ้น"
            description={`สำเร็จ ${summary.success} รายการ | ผิดพลาด ${summary.error} รายการ`}
            style={{ marginBottom: 16 }}
          />
          {summary.error > 0 && (
            <Table
              columns={logColumns}
              dataSource={importLog.filter((r) => r.status === 'error')}
              rowKey="excelRow"
              size="small"
              pagination={false}
              scroll={{ y: 220 }}
            />
          )}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>ปิด</Button>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (onImported) onImported();
                  onClose();
                }}
              >
                โหลดหน้าใหม่
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TempleStatisticsImportModal;